import type React from 'react';
import { useEffect, useState } from 'react';
import { env } from '@/config/env';
import { logError } from '@/utils/logger';

// Apple's "Sign in with Apple JS" injects a global. Minimal typing for what we use.
declare global {
  interface Window {
    AppleID?: {
      auth: {
        init: (config: {
          clientId: string;
          scope: string;
          redirectURI: string;
          usePopup: boolean;
        }) => void;
        signIn: () => Promise<{
          authorization: { id_token: string; code: string; state?: string };
        }>;
      };
    };
  }
}

const APPLE_SDK_SRC =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

interface AppleLoginButtonProps {
  onSuccess: (identityToken: string) => void;
  onError: () => void;
  disabled?: boolean;
}

/**
 * Renders the "Sign in with Apple" button for the manager web login.
 * Returns the Apple identity token (JWT) so the caller can exchange it at /managers/token/apple.
 * Renders nothing until the Services ID (VITE_APPLE_CLIENT_ID) is configured.
 */
const AppleLoginButton: React.FC<AppleLoginButtonProps> = ({
  onSuccess,
  onError,
  disabled,
}) => {
  const [ready, setReady] = useState(false);
  const clientId = env.APPLE_CLIENT_ID;
  const redirectURI = env.APPLE_REDIRECT_URI;

  useEffect(() => {
    if (!clientId || !redirectURI) return;

    // Guard against the SDK script loading after the component unmounts, which
    // would otherwise call setReady/onError on a dead component.
    let cancelled = false;

    const initialize = () => {
      if (cancelled) return;
      window.AppleID?.auth.init({
        clientId,
        scope: 'name email',
        redirectURI,
        usePopup: true,
      });
      setReady(true);
    };

    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${APPLE_SDK_SRC}"]`,
    );
    if (existing && window.AppleID) {
      initialize();
      return;
    }
    if (existing) {
      existing.addEventListener('load', initialize);
      return () => {
        cancelled = true;
        existing.removeEventListener('load', initialize);
      };
    }

    const script = document.createElement('script');
    script.src = APPLE_SDK_SRC;
    script.async = true;
    script.onload = initialize;
    script.onerror = () => {
      // A CDN load failure is not a sign-in attempt: leave the button disabled
      // (ready stays false) instead of surfacing a "login failed" toast to a
      // user who never clicked.
      logError('Apple SDK load failed', null);
    };
    document.body.appendChild(script);
    return () => {
      cancelled = true;
      script.onload = null;
      script.onerror = null;
    };
  }, [clientId, redirectURI, onError]);

  if (!clientId || !redirectURI) return null;

  const handleClick = async () => {
    try {
      const response = await window.AppleID?.auth.signIn();
      const idToken = response?.authorization?.id_token;
      if (idToken) {
        onSuccess(idToken);
      } else {
        onError();
      }
    } catch {
      // Apple throws on user-cancel too; surface a single error path to the caller.
      onError();
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || !ready}
      className="relative flex h-10 w-full items-center justify-center rounded border border-gray-300 bg-black text-sm font-gilroy font-medium text-white transition duration-200 hover:bg-black/90 disabled:opacity-60"
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        className="absolute left-4 h-[18px] w-[18px] fill-white"
      >
        <path d="M16.365 1.43c0 1.14-.42 2.21-1.13 3.02-.79.92-2.08 1.63-3.18 1.55-.13-1.1.43-2.27 1.1-3.04.77-.88 2.11-1.55 3.21-1.53zM20.6 17.3c-.55 1.27-.82 1.84-1.53 2.96-.99 1.56-2.39 3.5-4.12 3.51-1.54.02-1.94-1-4.03-.99-2.09.01-2.53 1.01-4.07.99-1.73-.02-3.05-1.78-4.04-3.33C-.07 16.94-.36 11.9 1.27 9.2 2.4 7.28 4.2 6.13 5.93 6.13c1.76 0 2.87 1 4.32 1 1.41 0 2.27-1 4.31-1 1.54 0 3.17.84 4.33 2.29-3.8 2.08-3.18 7.49.71 8.88z" />
      </svg>
      Continuar con Apple
    </button>
  );
};

export default AppleLoginButton;
